import { CheckCircleOutlined } from '@ant-design/icons';
import { Alert, Col, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import { RowTitles } from 'components/molecules/RowTitles';

import { EZpusobPrevzati } from 'services/dokumenty/prikazy';
import fileDownload from 'services/fileDownload';
// eslint-disable-next-line object-curly-newline
import {
    IMePozadavkyDetail,
    IMePozadavkyParams,
    IMePozadavkyTable,
    IMePozadavkyZadost,
    mePozadavkyDetailApi,
    mePozadavkyDownloadApi,
    mePozadavkyTableDataApi,
    mePozadavkyZruseniApi // eslint-disable-next-line object-curly-newline
} from 'services/statniSprava/mePozadavky';

import { ENotificationType } from 'enums/notificationType';
import { EProfilyTyp } from 'enums/profilyTyp';
import { EStavPozadavky } from 'enums/statniSprava/stavPozadavky';

import stavPozadavkySelect from 'data/codebooks/statniSprava/stavPozadavkySelect';
import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import getLanguageObject from 'utils/getLanguageObject';
import numberDecimalValidation from 'utils/validations/numberDecimalValidation';

interface IMePozadavky {}
interface ILoadingButtons {
    zruseni: boolean;
    zip: boolean;
    pdf: boolean;
    xml: boolean;
    html: boolean;
}

const MePozadavky: React.FC<IMePozadavky> = () => {
    const { language, NotificationComponent, aktivniFirma, menuProfile } = useContext(Context);
    const [form] = useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [lang, setLang] = useState<string>(language);
    const [inputNumberError, setInputNumberError] = useState<boolean>(false);
    const [loadingButtons, setLoadingButtons] = useState<ILoadingButtons>({
        zruseni: false,
        zip: false,
        pdf: false,
        xml: false,
        html: false
    });
    const [tableParams, setTableParams] = useState<IMePozadavkyParams>({
        limit: 10,
        order: 'oid:desc',
        mepozadavky: true
    });
    const [selectedRow, setSelectedRow] = useState<IMePozadavkyZadost>();
    const [rowDetails, setRowDetails] = useState<IMePozadavkyDetail>();
    const [tableData, setTableData] = useState<IMePozadavkyTable>();
    const [tableLoading, setTableLoading] = useState<boolean>();
    const [cancelled, setCancelled] = useState<boolean>();

    useEffect(() => {
        setTableData(undefined);
        tableParams && setTableLoading(true);
        tableParams &&
            mePozadavkyTableDataApi(tableParams)
                .then(data => setTableData(data))
                .finally(() => setTableLoading(false));
    }, [tableParams]);

    useEffect(() => {
        if (!rowDetails && selectedRow) {
            setLoading(true);
            window.scrollTo(0, 0);
            mePozadavkyDetailApi(selectedRow.id)
                .then(data => setRowDetails(data))
                .catch(() => setSelectedRow(undefined))
                .finally(() => setLoading(false));
        }
    }, [selectedRow, rowDetails]);

    const downloadPozadavek = (type: 'zip' | 'pdf' | 'xml' | 'html', oid?: string, langg?: string) => {
        if (selectedRow) {
            setLoadingButtons(pv => {
                return {
                    ...pv,
                    [type]: true
                };
            });

            mePozadavkyDownloadApi(selectedRow.id, type, oid, langg)
                .then(({ data }) => {
                    fileDownload(data.redirect);
                })
                .finally(() => {
                    setLoadingButtons(pv => {
                        return {
                            ...pv,
                            [type]: false
                        };
                    });
                    setRowDetails(undefined);
                });
        }
    };

    const zrusitPozadavek = () => {
        selectedRow &&
            setLoadingButtons(pv => {
                return {
                    ...pv,
                    zruseni: true
                };
            });
        selectedRow &&
            mePozadavkyZruseniApi(selectedRow.id)
                .then(() => {
                    NotificationComponent({
                        title: 'ZRUSENI_POZADAVKU',
                        description: 'ZRUSENI_POZADAVKU_OK',
                        type: ENotificationType.SUCCESS
                    });
                    setSelectedRow(undefined);
                    setTableParams(pv => {
                        return {
                            ...pv
                        };
                    });
                })
                .finally(() => {
                    setLoadingButtons(pv => {
                        return {
                            ...pv,
                            zruseni: false
                        };
                    });
                    setCancelled(true);
                });
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const onFinish = (values: any) => {
        setTableParams({
            limit: 10,
            order: 'oid:desc',
            mepozadavky: values.mepozadavky || true,
            ...(values.id && {
                id: values.id
            }),
            ...(values.sluzba && {
                sluzba: values.sluzba
            }),
            ...(values.stav && {
                stav: values.stav
            }),
            ...(values.datumvypisu && {
                datumvypisu: dayjs(values.datumvypisu).format('YYYY-MM-DD')
            }),
            ...(values.datumvypisu2 && {
                datumvypisu2: dayjs(values.datumvypisu2).format('YYYY-MM-DD')
            })
        });
    };

    const rowOdpovediColumns = [
        {
            title: <TranslateText>ME_POZADAVKY_PRIJMENO</TranslateText>,
            dataIndex: 'prijmeni',
            key: 'prijmeni'
        },
        {
            title: <TranslateText>ME_POZADAVKY_JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno'
        },
        {
            dataIndex: 'oid',
            key: 'oid',
            render: (oid: string) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-evenly'
                    }}
                >
                    <AnchorComponent onClick={() => downloadPozadavek('pdf', oid, lang)} id="mePozadavkyDlPdfAnchor">
                        <TranslateText>STAHNOUT_PDF</TranslateText>
                    </AnchorComponent>
                    <AnchorComponent onClick={() => downloadPozadavek('xml', oid)} id="mePozadavkyDlXmlAnchor">
                        <TranslateText>SRD_STAHNOUT_XML</TranslateText>
                    </AnchorComponent>
                    <AnchorComponent onClick={() => downloadPozadavek('html', oid)} id="mePozadavkyDlHtmlAnchor">
                        <TranslateText>STAHNOUT_HTML</TranslateText>
                    </AnchorComponent>
                </div>
            ),
            align: 'center' as 'center'
        }
    ];

    const rowDetailsColumns = [
        {
            title: <TranslateText>DATUM</TranslateText>,
            dataIndex: 'datum',
            key: 'datum',
            render: (datum: string) => dayjs(datum).format('DD.MM.YYYY HH:mm')
        },
        {
            title: <TranslateText>UDALOST</TranslateText>,
            dataIndex: 'typ',
            key: 'typ',
            render: (typ: string) => <TranslateText>{`ME_POZADAVKY_ZADOST_TYP_${typ}`}</TranslateText>
        },
        {
            title: <TranslateText>UZIVATEL</TranslateText>,
            dataIndex: 'osoba',
            key: 'osoba'
        }
    ];

    const columns = [
        {
            title: <TranslateText>PODANO</TranslateText>,
            dataIndex: 'datumpodani',
            key: 'datumpodani',
            render: (datumpodani: string) => dayjs(datumpodani).format('DD.MM.YYYY HH:mm')
        },
        {
            title: <TranslateText>IDENTIFIKATOR</TranslateText>,
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: <TranslateText>KOD_SLUZBY</TranslateText>,
            dataIndex: 'sluzba',
            key: 'sluzba'
        },

        {
            title: <TranslateText>STAV</TranslateText>,
            dataIndex: 'stav',
            key: 'stav',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            render: (text: EStavPozadavky) => {
                return <TranslateText>{text}</TranslateText>;
            }
        },
        {
            title: <TranslateText>PODAL</TranslateText>,
            dataIndex: 'podal',
            key: 'podal'
        },
        {
            title: <TranslateText>ZOBRAZENO</TranslateText>,
            dataIndex: 'precteno',
            key: 'precteno',
            render: (text: boolean) =>
                text && (
                    <span
                        style={{
                            color: 'green'
                        }}
                    >
                        <CheckCircleOutlined />
                    </span>
                ),
            align: 'center' as 'center'
        },
        {
            dataIndex: 'id',
            key: 'id',
            render: (_: number, row: IMePozadavkyZadost, index: number) => (
                <AnchorComponent onClick={() => setSelectedRow(row)} id={`mePozadavkyZobrazitBtn${index}`}>
                    <TranslateText>ZOBRAZIT</TranslateText>
                </AnchorComponent>
            ),
            align: 'center' as 'center'
        }
    ];

    const renderF45 = () =>
        rowDetails && (
            <>
                {rowDetails.f45?.zpusob && !rowDetails.f45?.dlt && (
                    <RowTitles
                        paragraph={true}
                        firstText={'PRIKAZ_JINAOSOBA_VYPIS_VYSTUP'}
                        secondText={String('PRIKAZ_JINAOSOBA_VYPIS_VYSTUP_' + rowDetails.f45?.zpusob)}
                    />
                )}
                {rowDetails.f45?.prijmeni && (
                    <Title
                        level={5}
                        text={
                            rowDetails.f45.zpusob === EZpusobPrevzati.OSOBUSB ||
                            rowDetails.f45.zpusob === EZpusobPrevzati.OSOBLIST
                                ? 'PRIKAZ_JINAOSOBA_VYPIS_DORUCENI_OSOB'
                                : 'PRIKAZ_JINAOSOBA_VYPIS_DORUCENI_POST'
                        }
                    />
                )}
                {rowDetails.f45?.nazev && (
                    <RowTitles
                        paragraph={true}
                        firstText={'EMP_PODANE_ZADOSTI_NAZEV_SPOLECNOSTI'}
                        secondText={String(rowDetails.f45?.nazev)}
                    />
                )}
                {(rowDetails?.jmeno || rowDetails.f45?.jmeno) && (
                    <RowTitles
                        paragraph={true}
                        firstText={'ME_POZADAVKY_JMENO'}
                        secondText={String(rowDetails.jmeno || rowDetails.f45?.jmeno)}
                    />
                )}
                {(rowDetails?.prijmeno || rowDetails.f45?.prijmeni) && (
                    <RowTitles
                        paragraph={true}
                        firstText={'ME_POZADAVKY_PRIJMENO'}
                        secondText={String(rowDetails.prijmeno || rowDetails.f45?.prijmeni)}
                    />
                )}
                {rowDetails.f45?.rodnecislo && (
                    <RowTitles
                        paragraph={true}
                        firstText={'FORMULARE_RODNE_CISLO_CR'}
                        secondText={String(rowDetails.f45?.rodnecislo)}
                    />
                )}
                {rowDetails.f45?.adma && (
                    <RowTitles paragraph={true} firstText={'ULICE_CP'} secondText={String(rowDetails.f45?.adma)} />
                )}
                {rowDetails.f45?.mema && (
                    <RowTitles paragraph={true} firstText={'MESTO'} secondText={String(rowDetails.f45?.mema)} />
                )}
                {rowDetails.f45?.pscm && (
                    <RowTitles paragraph={true} firstText={'PSC'} secondText={String(rowDetails.f45?.pscm)} />
                )}
                {rowDetails.f45?.kost && (
                    <RowTitles paragraph={true} firstText={'STAT_SIDLA'} secondText={String(rowDetails.f45?.kost)} />
                )}
                {rowDetails.f45?.email && (
                    <RowTitles
                        paragraph={true}
                        firstText={'PRIKAZ_JINAOSOBA_VYPIS_DORUCENI_EMAIL'}
                        secondText={String(rowDetails.f45?.email)}
                    />
                )}
            </>
        );

    return (
        <>
            {!selectedRow && (
                <>
                    {(aktivniFirma?.profil === '39' || menuProfile?.dluznik) && (
                        <Alert
                            message={<TranslateText>DLUZNIK_MSG</TranslateText>}
                            type="error"
                            style={{
                                marginBottom: 16
                            }}
                        />
                    )}
                    {menuProfile?.vypis && menuProfile?.neplatneaml && aktivniFirma?.typ !== EProfilyTyp.ADMEMIT && (
                        <Alert
                            message={<TranslateText>NEPLATNE_AML_ERR</TranslateText>}
                            type="error"
                            style={{
                                marginBottom: 16
                            }}
                        />
                    )}
                    <Title level={3} text={'ME_POZADAVKY_TITLE'} />
                    <FormWrapper onFinish={onFinish} outForm={form}>
                        <Row gutter={8}>
                            <Col span={3}>
                                <InputNumberComponent
                                    label="IDENTIFIKATOR"
                                    name="id"
                                    precision={0}
                                    notRequired={true}
                                    maxLength={10}
                                    rules={numberDecimalValidation(setInputNumberError)}
                                    extramessage={inputNumberError ? 'NEVALIDNI_NUM' : ''}
                                />
                            </Col>
                            <Col span={3}>
                                <InputComponent
                                    label="KOD_SLUZBY"
                                    type="text"
                                    name="sluzba"
                                    maxLength={5}
                                    notRequired={true}
                                />
                            </Col>
                            <Col>
                                <DatePickerComponent label="PODANO_OD" name="datumvypisu" notRequired={true} />
                            </Col>
                            <Col>
                                <DatePickerComponent label="PODANO_DO" name="datumvypisu2" notRequired={true} />
                            </Col>
                            <Col span={5}>
                                <SelectComponent
                                    name="stav"
                                    labelParagraph="STAV"
                                    notRequired={true}
                                    isFormItem={true}
                                    data={stavPozadavkySelect}
                                />
                            </Col>
                            <SelectComponent
                                name="mepozadavky"
                                labelParagraph="ME_POZADAVKY_JEN_MOJE"
                                notRequired={true}
                                isFormItem={true}
                                defaultValue={'true'}
                                data={trueFalseSelect}
                            />
                        </Row>
                        <Row
                            className="button-row-modal"
                            style={{
                                position: 'relative',
                                top: inputNumberError ? -8 : -16
                            }}
                        >
                            <ButtonComponent
                                id="mepozadavkyTableFiltrovat"
                                buttontext={'FILTROVAT'}
                                onClick={() => {
                                    onFinish(form.getFieldsValue());
                                }}
                            />
                            <ButtonComponent
                                id="mepozadavkyTableClearFiltr"
                                buttontext={'VYMAZAT_FILTR'}
                                onClick={() => {
                                    form.resetFields();
                                    setTableParams({
                                        limit: 10,
                                        order: 'oid:desc',
                                        mepozadavky: false
                                    });
                                }}
                                htmlType="button"
                            />
                        </Row>
                    </FormWrapper>
                    <TableComponent
                        onRow={record => {
                            return {
                                onClick: () => setSelectedRow(record)
                            };
                        }}
                        rowKey={e => e.id}
                        columns={columns}
                        dataSource={tableData?.zadosti}
                        loading={tableLoading}
                        locale={{
                            emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                        }}
                        pagination={{
                            total: tableData?.count,
                            onChange: page =>
                                setTableParams(pv => {
                                    return {
                                        ...pv,
                                        offset: (page - 1) * 10
                                    };
                                }),
                            current: (tableParams?.offset ? tableParams.offset : 0) / 10 + 1,
                            showSizeChanger: false
                        }}
                    />
                </>
            )}
            {selectedRow && (
                <>
                    <LoadingWrapper
                        loading={selectedRow.stav === EStavPozadavky.ZODPOVEZEN ? false : loading}
                        style={{
                            paddingTop: '40px'
                        }}
                    >
                        {rowDetails && rowDetails.stav !== EStavPozadavky.ZODPOVEZEN && (
                            <>
                                <Title level={3} text={'ME_POZADAVKY_DETAIL_TITLE'} />
                                {rowDetails?.datumvypisu && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={
                                            ['F5', 'F6', 'F7', 'F8', 'F9', 'F38', 'F39', 'F50'].indexOf(
                                                rowDetails.sluzba
                                            ) !== -1
                                                ? 'ME_POZADAVKY_DATUM1_POZADOVANE'
                                                : 'ME_POZADAVKY_DATUM1'
                                        }
                                        secondText={String(dayjs(rowDetails.datumvypisu).format('DD.MM.YYYY'))}
                                    />
                                )}
                                {rowDetails?.datumvypisu2 && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={
                                            ['F5', 'F6', 'F7', 'F8', 'F9', 'F38', 'F39', 'F50'].indexOf(
                                                rowDetails.sluzba
                                            ) !== -1
                                                ? 'ME_POZADAVKY_DATUM2_DUALNI'
                                                : 'ME_POZADAVKY_DATUM2'
                                        }
                                        secondText={String(dayjs(rowDetails.datumvypisu2).format('DD.MM.YYYY'))}
                                    />
                                )}
                                {rowDetails?.cislojednacispisovaznacka && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={
                                            ['F25', 'F26', 'F27', 'F8'].indexOf(rowDetails.sluzba) !== -1
                                                ? 'ME_POZADAVKY_CISLO_JEDNACI'
                                                : ['F8', 'F5'].indexOf(rowDetails.sluzba) !== -1
                                                  ? 'ME_POZADAVKY_CISLO_ZNACKA'
                                                  : 'ME_POZADAVKY_CISLO_JEDNACI_ZNACKA'
                                        }
                                        secondText={String(rowDetails.cislojednacispisovaznacka)}
                                    />
                                )}
                                {rowDetails?.identifikacesoudu && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_INDENTIFIKACE_SOUDU'}
                                        secondText={String(rowDetails.identifikacesoudu)}
                                    />
                                )}
                                {rowDetails?.id1zastavy && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_ID1'}
                                        secondText={String(rowDetails.id1zastavy)}
                                    />
                                )}
                                {rowDetails?.id2zastavy && (
                                    <InputComponent
                                        disabled={true}
                                        label={'ME_POZADAVKY_ID2'}
                                        type="text"
                                        name="ME_POZADAVKY_ID2"
                                        notRequired={true}
                                        defaultValue={rowDetails.id2zastavy}
                                    />
                                )}
                                {rowDetails?.cislozastavy && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_CISLO_ZMLUVY'}
                                        secondText={String(rowDetails.cislozastavy)}
                                    />
                                )}
                                {rowDetails?.idzastavveritele && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_IDIC_VERITEL'}
                                        secondText={String(rowDetails.idzastavveritele)}
                                    />
                                )}
                                {rowDetails?.cisloppn && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_CISLO_PPN'}
                                        secondText={String(rowDetails.cisloppn)}
                                    />
                                )}
                                {rowDetails?.idsubjppn && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_IDIC_SUBJPPN'}
                                        secondText={String(rowDetails.idsubjppn)}
                                    />
                                )}
                                {rowDetails?.kategoriemajitle && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_KATEGORIE_MAJITELE'}
                                        secondText={String(
                                            rowDetails.kategoriemajitle === '1'
                                                ? getLanguageObject(language).FYZICKA_OSOBA
                                                : getLanguageObject(language).PRAVNICKA_OSOBA
                                        )}
                                    />
                                )}
                                {rowDetails?.idmajitel && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={
                                            ['F24', 'F9', 'F28', 'F20'].indexOf(rowDetails.sluzba) !== -1
                                                ? 'ME_POZADAVKY_IDIC_MAJITEL'
                                                : ['F17', 'F18', 'F27', 'F6', 'F7'].indexOf(rowDetails.sluzba) !== -1
                                                  ? 'ME_POZADAVKY_IDIC_MAJITEL2'
                                                  : ['F50'].indexOf(rowDetails.sluzba) !== -1
                                                    ? 'ME_POZADAVKY_ID_SUBJEKTU'
                                                    : 'ME_POZADAVKY_IDENTIFIKATOR_MAJITELE'
                                        }
                                        secondText={String(rowDetails.idmajitel)}
                                    />
                                )}
                                {rowDetails?.idosobacdcp && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_IDENTIFIKATOR_OSOBYCDCP'}
                                        secondText={String(rowDetails.idosobacdcp)}
                                    />
                                )}
                                {rowDetails?.idzastavdluznika && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_IDIC_DLUZNIK'}
                                        secondText={String(rowDetails.idzastavdluznika)}
                                    />
                                )}
                                {rowDetails?.predcisli && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_PREDCISLI_UCTU'}
                                        secondText={String(rowDetails.predcisli)}
                                    />
                                )}
                                {rowDetails?.cislouctu && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'CISLO_UCTU'}
                                        secondText={String(rowDetails.cislouctu)}
                                    />
                                )}
                                {rowDetails?.isinemise && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ISIN'}
                                        secondText={String(rowDetails.isinemise)}
                                    />
                                )}
                                {rowDetails?.kategorie && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={
                                            ['F38', 'F39'].indexOf(rowDetails.sluzba) !== -1
                                                ? 'ME_POZADAVKY_KATEGORIE'
                                                : 'ME_POZADAVKY_KATEGORIE_TYP'
                                        }
                                        secondText={String(
                                            ['F20'].indexOf(rowDetails.sluzba) !== -1
                                                ? rowDetails.kategorie
                                                : getLanguageObject(language)[
                                                      `ME_POZADAVKY_KATEGORIE_${rowDetails.kategorie}`
                                                  ]
                                        )}
                                    />
                                )}
                                {rowDetails?.druhvypisu && ['F9'].indexOf(rowDetails.sluzba) === -1 && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_DRUH_VYPISU'}
                                        secondText={String(rowDetails.druhvypisu)}
                                    />
                                )}

                                {typeof rowDetails.f45?.zast === 'boolean' && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'F45_ZAST'}
                                        secondText={String(rowDetails.f45.zast ? 'ANO' : 'NE')}
                                    />
                                )}
                                {typeof rowDetails.f45?.esm === 'boolean' && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'F45_ESM'}
                                        secondText={String(rowDetails.f45.esm ? 'ANO' : 'NE')}
                                    />
                                )}
                                {typeof rowDetails.f45?.majucet === 'boolean' && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'F45_MAJUCET'}
                                        secondText={String(rowDetails.f45.majucet ? 'ANO' : 'NE')}
                                    />
                                )}
                                {typeof rowDetails.f45?.bankucet === 'boolean' && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'F45_BANKUCET'}
                                        secondText={String(rowDetails.f45.bankucet ? 'ANO' : 'NE')}
                                    />
                                )}
                                {renderF45()}

                                {rowDetails?.idspravceuctu && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_IDIC_SPRAVCE'}
                                        secondText={String(rowDetails.idspravceuctu)}
                                    />
                                )}
                                {rowDetails?.idspravce && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_INDENTIFIKATOR_SPRAVCE'}
                                        secondText={String(rowDetails.idspravce)}
                                    />
                                )}
                                {rowDetails?.rolesubjektu && (
                                    <RowTitles
                                        paragraph={true}
                                        firstText={'ME_POZADAVKY_ROLE'}
                                        secondText={String(rowDetails.rolesubjektu)}
                                    />
                                )}
                            </>
                        )}
                    </LoadingWrapper>
                    <Title
                        level={3}
                        text={'ME_POZADAVKY_DETAIL_POZADAVEK'}
                        style={{
                            paddingTop: 10
                        }}
                    />
                    {(rowDetails?.odpovedi || []).length > 0 && (
                        <Alert
                            message={<TranslateText>WARNING_MEPOZADAVKY_RC</TranslateText>}
                            type={'warning'}
                            style={{
                                marginTop: 10,
                                marginBottom: 16
                            }}
                        />
                    )}
                    {selectedRow.id && (
                        <RowTitles paragraph={true} firstText={'IDENTIFIKATOR'} secondText={String(selectedRow.id)} />
                    )}
                    {selectedRow.sluzba && (
                        <RowTitles paragraph={true} firstText={'KOD_SLUZBY'} secondText={selectedRow.sluzba} />
                    )}
                    {selectedRow.stav && (
                        <RowTitles
                            paragraph={true}
                            firstText={'STAV'}
                            secondText={cancelled ? 'ZRUSEN' : selectedRow.stav}
                        />
                    )}
                    {selectedRow.datumpodani && (
                        <RowTitles
                            paragraph={true}
                            firstText={'PODANO'}
                            secondText={dayjs(selectedRow.datumpodani).format('DD.MM.YYYY HH:mm')}
                        />
                    )}
                    {renderF45()}
                    {selectedRow.stav === EStavPozadavky.ZODPOVEZEN &&
                        !cancelled &&
                        (rowDetails?.odpovedi || []).length < 1 && (
                            <Row>
                                <Col span={7}>
                                    <Paragraph
                                        text={'LANG_SELECT_PDF'}
                                        style={{
                                            marginRight: 16
                                        }}
                                    />
                                </Col>
                                <Col
                                    span={4}
                                    style={{
                                        position: 'relative',
                                        right: '10px'
                                    }}
                                >
                                    <SelectComponent
                                        bordered={false}
                                        size="small"
                                        notRequired={true}
                                        data={[
                                            {
                                                value: 'cz',
                                                labelcz: 'CESTINA'
                                            },
                                            {
                                                value: 'en',
                                                labelcz: 'ENGLISH'
                                            }
                                        ]}
                                        value={lang}
                                        onChange={val => setLang(val)}
                                        name="lang"
                                    />
                                </Col>
                            </Row>
                        )}
                    <Row className="button-row">
                        {selectedRow.stav === EStavPozadavky.CEKANAZPRAC && (
                            <ButtonComponent
                                buttontext="ZRUSIT"
                                id="mePozadavkyZrusitBtn"
                                onClick={zrusitPozadavek}
                                loading={loadingButtons.zruseni}
                            />
                        )}
                        {selectedRow.stav === EStavPozadavky.ZODPOVEZEN &&
                            !cancelled &&
                            (!rowDetails?.f45 || rowDetails?.f45?.zpusob === 'MD') && (
                                <>
                                    {selectedRow.vystupzip ? (
                                        <>
                                            <ButtonComponent
                                                buttontext="STAHNOUT_ZIP"
                                                onClick={() => downloadPozadavek('zip')}
                                                loading={loadingButtons.zip}
                                                id="mePozadavkyDlZipBtn"
                                            />
                                            <ButtonComponent
                                                buttontext="STAHNOUT_HTML"
                                                onClick={() => downloadPozadavek('html')}
                                                loading={loadingButtons.html}
                                                id="mePozadavkyDlHtmlAltBtn"
                                            />
                                        </>
                                    ) : (rowDetails?.odpovedi || []).length < 1 ? (
                                        <>
                                            <ButtonComponent
                                                buttontext="STAHNOUT_PDF"
                                                onClick={() => downloadPozadavek('pdf', undefined, lang)}
                                                loading={loadingButtons.pdf}
                                                id="mePozadavkyDlPdfBtn"
                                            />
                                            <ButtonComponent
                                                buttontext="SRD_STAHNOUT_XML"
                                                onClick={() => downloadPozadavek('xml')}
                                                loading={loadingButtons.xml}
                                                id="mePozadavkyDlXmlBtn"
                                            />
                                            <ButtonComponent
                                                buttontext="STAHNOUT_HTML"
                                                onClick={() => downloadPozadavek('html')}
                                                loading={loadingButtons.html}
                                                id="mePozadavkyDlHtmlBtn"
                                            />
                                        </>
                                    ) : null}
                                </>
                            )}
                    </Row>
                    {(rowDetails?.odpovedi || []).length > 0 && (
                        <>
                            <Title text="PREHLED_ODPOVEDI" level={5} />
                            <TableComponent
                                dataSource={rowDetails?.odpovedi}
                                loading={!rowDetails || loadingButtons.pdf || loadingButtons.xml || loadingButtons.html}
                                columns={rowOdpovediColumns}
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                                pagination={{
                                    total: rowDetails?.odpovedi.length,
                                    defaultPageSize: 10,
                                    showSizeChanger: false
                                }}
                            />
                        </>
                    )}
                    {selectedRow.stav === EStavPozadavky.ZODPOVEZEN && (
                        <>
                            <Title text="ME_POZADAVKY_PREHLED" level={5} />
                            <TableComponent
                                dataSource={rowDetails?.pristupy}
                                loading={!rowDetails}
                                columns={rowDetailsColumns}
                                locale={{
                                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                }}
                                pagination={{
                                    total: rowDetails?.pristupy.length,
                                    defaultPageSize: 10,
                                    showSizeChanger: false
                                }}
                            />
                        </>
                    )}
                    <ButtonComponent
                        buttontext="ZPET"
                        type="default"
                        id="mepozadavkyZpetBtn"
                        onClick={() => {
                            setSelectedRow(undefined);
                            setRowDetails(undefined);
                            setCancelled(false);
                            window.scrollTo(0, 0);
                        }}
                    />
                </>
            )}
        </>
    );
};

export default MePozadavky;
